import { Component, EventEmitter, HostListener, Inject, Input, OnDestroy, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { APP_CONFIG } from '../../app.config';
import { AssistantService } from '../../common/assistant.service';
import { AppConfig } from '../../models/config/app-config';
import { FormSubGroupControl } from '../../models/formControls/FormSubGroupControl';
import { VisumErfassungComponentBase } from '../visum-erfassung-component-base';

const isErwerb = (r: any) => r?.dVisumZweck === 'Erwerbstätigkeit';

@Component({
  selector: 'app-visum-erfassung',
  templateUrl: './visum-erfassung.component.html',
  styleUrls: ['./visum-erfassung.component.scss']
})
export class VisumErfassungComponent extends VisumErfassungComponentBase implements OnDestroy {
  form: FormGroup;
  validities: any;
  isDokumenteActive: boolean;
  isLichtbildActive: boolean;
  isFingerabdruckActive: boolean;
  isErwerbstaetigkeit = false;
  readonly reisedatenSub?: Subscription;

  referenzArt: string = undefined;
  isRechtAufFreizuegigkeit = false;

  defaults: any;
  formtyp: string;

  @Output()
  formChange = new EventEmitter<any>();

  @Output()
  validitiesChange = new EventEmitter<any>();

  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event && event.key === 'Escape') {
      this.assistantService.disable();
    }
  }

  constructor(fb: FormBuilder,
              @Inject(APP_CONFIG) public appConfig: AppConfig,
              private assistantService: AssistantService) {
    super();

    this.formtyp = appConfig.umgebung;
    this.isDokumenteActive = appConfig.isDokumenteActive;
    this.isLichtbildActive = appConfig.isLichtbildActive;
    this.isFingerabdruckActive = appConfig.isFingerabdruckActive;

    this.form = fb.group({
      angabenZurPerson: new FormSubGroupControl(),
      kontaktdaten: new FormSubGroupControl(),
      ausweispapiere: new FormSubGroupControl(),
      reisedaten: new FormSubGroupControl(),
      referenz: new FormSubGroupControl(),
      lebensunterhaltAufenthaltsdetails: new FormSubGroupControl(),
      dokumente: new FormSubGroupControl(),
      lichtbild: new FormSubGroupControl(),
      fingerabdruck: new FormSubGroupControl(),
      zusaetzlicheAngaben: new FormSubGroupControl(),
      kostenuebernahme: new FormSubGroupControl()
    });

    this.isErwerbstaetigkeit = isErwerb(this.form.value?.reisedaten);
    this.form.valueChanges.subscribe(value => {
      this.isErwerbstaetigkeit = isErwerb(value?.reisedaten);
      this.updateValidities();
      this.formChange.next(this.form.value);
    });

    this.form.valueChanges.subscribe(value => {
      if (this.form.value.angabenZurPerson && this.isRechtAufFreizuegigkeit !== this.form.value.angabenZurPerson.isRechtAufFreizuegigkeit) {
        this.isRechtAufFreizuegigkeit = this.form.value.angabenZurPerson.isRechtAufFreizuegigkeit;
        this.showOrHideReferenz(this.isRechtAufFreizuegigkeit);
      }

      if (!this.form.controls.referenz.disabled && this.form.value.referenz) {
        if (this.referenzArt !== this.form.value.referenz.art) {
          this.referenzArt = this.form.value.referenz.art;
          this.showOrHideKostenuebername(this.referenzArt);
        }
      }

      this.updateValidities();
      this.formChange.next(this.form.value);
    });

    this.defaults = appConfig.defaults;
    this.visumErfassung = {};

    function collectValidity(form) {
      const res = {};
      for (const controlName of Object.keys(form.controls)) {
        res[controlName] = { status: form.controls[controlName].status, errors: form.controls[controlName].errors };
      }
      return res;
    }
    this.enableOrDisableComponents();
  }

  @Input()
  set visumErfassung(value: any) {
    const v = this.setDefaults(value, this.defaults);
    if (value == null || Object.keys(value).length === 0) {
      // wschwit1: wir müssen zwei mal reseten, wegen MEK-3028
      // ggf. mit writeValue lösen
      this.form.reset(v, { emitEvent: false });
      this.form.reset(v, { emitEvent: false });
    } else {
      this.form.setValue(v, { emitEvent: false });

      // Wir schalten Bereiche ab, die per Konfiguration abgeschaltet werden
      this.enableOrDisableComponents();

      // Wir schalten Bereiche ab, die datengetrieben abgeschaltet werden
      // Dies sollte eigentlich in Subscriptions (s. constructor) stattfinden,
      // findet jedoch nicht statt, auch nicht mit emitEvent:true. Deshalb hier nochmal.
      this.isRechtAufFreizuegigkeit = this.form.value.angabenZurPerson.isRechtAufFreizuegigkeit;
      this.showOrHideReferenz(this.isRechtAufFreizuegigkeit);
    }
    this.updateValidities();
    this.isErwerbstaetigkeit = isErwerb(this.form.value?.reisedaten);
  }

  ngOnDestroy() {
    this.reisedatenSub?.unsubscribe();
  }

  private setDefaults(oldValue, defaults) {
    oldValue = oldValue || {};
    const res = JSON.parse(JSON.stringify(oldValue)); // deep copy
    const properties = Object.keys(defaults || {});
    for (const property of properties) {
      if (!res.hasOwnProperty(property) || res[property] == null) {
        res[property] = defaults[property];
      } else if (res[property] != null && typeof oldValue[property] === 'object') {
        res[property] = this.setDefaults(res[property], defaults[property]);
      }
    }
    return res;
  }

  private showOrHideReferenz(isRechtAufFreizuegigkeit: boolean): void {

    if (isRechtAufFreizuegigkeit === true) {
      this.form.controls.referenz.disable();
      this.referenzArt = undefined;
    } else {
      if (this.form.controls.referenz.disabled) {
        this.form.controls.referenz.enable();
      }
      this.referenzArt = this.form.value.referenz.art;
    }
    this.showOrHideKostenuebername(this.referenzArt);
  }

  private showOrHideKostenuebername(referenzArt: string) {
    if (this.appConfig.isKostenuebernahmeActive === true && referenzArt && referenzArt !== '1') {
      // Wir machen diese Prüfung, weil wir das zweifache Aufrufen von enable() verhindern wollen.
      // Dies führt dazu, dass Felder angezeigt werden, die ausgeblendet sein sollen.
      if (this.form.controls.kostenuebernahme.disabled) {
        this.form.controls.kostenuebernahme.enable();
      }
    } else {
      this.form.controls.kostenuebernahme.disable();
    }
  }

  private updateValidities(): void {
    const form = this.form;
    const subformState = collectSubformState(form);
    this.validities = {
      angabenZurPerson: getFormGroupValidity('angabenZurPerson'),
      kontaktdaten: getFormGroupValidity('kontaktdaten'),
      fingerabdruck: getFormGroupValidity('fingerabdruck'),
      lichtbild: getFormGroupValidity('lichtbild'),
      dokumente: getFormGroupValidity('dokumente'),
      ausweispapiere: getFormGroupValidity('ausweispapiere'),
      reisedaten: getFormGroupValidity('reisedaten'),
      referenz: getFormGroupValidity('referenz'),
      lebensunterhaltAufenthaltsdetails: getFormGroupValidity('lebensunterhaltAufenthaltsdetails'),
      zusaetzlicheAngaben: getFormGroupValidity('zusaetzlicheAngaben'),
      kostenuebernahme: getFormGroupValidity('kostenuebernahme')
    };
    this.validitiesChange.emit(this.validities);

    function getFormGroupValidity(groupName) {
      const formState = subformState.controls[groupName];
      if (!formState || formState.disabled) {
        return undefined;
      }
      return {
        valid: formState ? (formState.dirty || formState.touched) && formState.valid : false,
        validationErrorKeys: formState.errorKeys
      };
    }

    function collectSubformState(formGroup: FormGroup): any {
      const errorKeys = [];
      if (formGroup.errors) {
        for (const errorKey of Object.keys(formGroup.errors)) {
          errorKeys.push(errorKey);
        }
      }
      const res = {
        disabled: formGroup.disabled,
        dirty: formGroup.dirty,
        touched: formGroup.touched,
        valid: formGroup.valid,
        controls: {},
        errorKeys: errorKeys
      };
      for (const controlName of Object.keys(formGroup.controls)) {
        collectStateOfControl(formGroup, controlName, res);
      }
      return res;
    }

    function collectStateOfControl(
      formGroup: FormGroup,
      controlName: string,
      res: { dirty: boolean; valid: boolean; touched: boolean; controls: {}; disabled: boolean; errorKeys: any[] }
    ) {
      const control = formGroup.controls[controlName];
      if (control.disabled) {
        // Angular betrachtet disabled Elemente immer als valid=false, nimmt sie aber aus
        // der Gültigkeitsberechnung für die FormGroup heraus. Deswegen dürfen wir sie hier
        // auch nicht berücksichtigen.
        return;
      }
      let state;
      if (control instanceof FormSubGroupControl) {
        const subgroupControl = <FormSubGroupControl>control;
        if (subgroupControl._valueAccessor) {
          state = collectSubformState(subgroupControl._valueAccessor.form);
        } else {
          state = { dirty: false, touched: false, valid: true, errorKeys: [] };
        }
      } else if (control instanceof FormArray) {
        state = { dirty: control.dirty, touched: control.touched, valid: control.valid, errorKeys: [] };
        for (const item of control.controls) {
          collectStateOfSubgroupControl(item, state);
        }
      } else {
        state = { dirty: control.dirty, touched: control.touched, valid: control.valid, errorKeys: [] };
      }
      res.controls[controlName] = state;
      res.dirty = res.dirty || state.dirty;
      res.touched = res.touched || state.touched;
      res.valid = res.valid && state.valid;
      state.errorKeys.forEach(key => {
        res.errorKeys.push(key);
      });
    }

    function collectStateOfSubgroupControl(item: AbstractControl, state) {
      const subgroupControl = <FormSubGroupControl>item;
      if (subgroupControl._valueAccessor && subgroupControl._valueAccessor.form) {
        const itemState = collectSubformState(subgroupControl._valueAccessor.form);
        state.dirty = state.dirty || itemState.dirty;
        state.touched = state.touched || itemState.touched;
        state.valid = state.valid && itemState.valid;
        itemState.errorKeys.forEach(key => {
          state.errorKeys.push(key);
        });
      }
    }
  }

  private enableOrDisableComponents() {
    if (this.appConfig.isLebensunterhaltAufenthaltsdetailsActive !== true) {
      this.form.controls.lebensunterhaltAufenthaltsdetails.disable();
    }

    if (this.appConfig.isZusaetzlicheAngabenActive !== true) {
      this.form.controls.zusaetzlicheAngaben.disable();
    }

    if (this.appConfig.isKostenuebernahmeActive !== true) {
      this.form.controls.kostenuebernahme.disable();
    }

    if (this.appConfig.isDokumenteActive !== true) {
      this.form.controls.dokumente.disable();
    }

    if (this.appConfig.isLichtbildActive !== true) {
      this.form.controls.lichtbild.disable();
    }

    if (this.appConfig.isFingerabdruckActive !== true) {
      this.form.controls.fingerabdruck.disable();
    }

  }

}
